import React, { useCallback, useState } from 'react'

import { Box } from '@mui/material'

import PaginatedPage from '../../shared/components/PaginatedPage/PaginatedPage.component'
import FulfillmentOrderAccordion from './components/FulfillmentOrderAccordion/FulfillmentOrderAccordion.component'

import env from '../../networkRequests/apiClient/env.config'
import { getFulfillmentOrders } from '../../redux/states/fulfillmentOrders/getFulfillmentOrders/getFulfillmentOrders.slice'
import {
  FulfillmentOrder,
  GetFulfillmentOrdersRequest
} from '../../redux/states/fulfillmentOrders/getFulfillmentOrders/getFulfillmentOrders.types'
import { RootState } from '../../redux/store/store.types'
import router from '../../router/functions/router.functions'
import routes from '../../router/routes.dictionary'
import { EmptyStateProps } from '../../shared/components/EmptyState/EmptyState.types'
import { FilterTab, PaginatedResponse, SearchBarProps } from '../../shared/components/PaginatedPage/PaginatedPage.types'
import {
  FilterSectionConfig,
  Filters
} from '../../shared/components/UniversalFilterSideBar/UniversalFilterSideBar.types'
import { useAppDispatch, useAppSelector } from '../../shared/hooks/redux.hooks'
import styles from './FulfillmentOrders.styles'

const FulfillmentOrders: React.ForwardRefRenderFunction<HTMLDivElement> = (_props, _ref) => {
  const [numberOfItems, setNumberOfItems] = useState(0)

  const { loading } = useAppSelector((state: RootState) => state.getFulfillmentOrders)

  const pageSize = env.DEFAULT_PAGE_SIZE || 10

  const dispatch = useAppDispatch()
  const navigate = router.navigate()

  const fetchFulfillmentOrders = useCallback(
    async (page: number, onSuccess?: () => void): Promise<PaginatedResponse<FulfillmentOrder>> => {
      return new Promise((resolve) => {
        const requestPayload: GetFulfillmentOrdersRequest = {
          page,
          limit: Number(pageSize),
          sortBy: 'created_at',
          sortOrder: 'desc'
        }

        dispatch(
          getFulfillmentOrders({
            request: requestPayload,
            onSuccess: () => {
              onSuccess?.()
            }
          })
        )
          .unwrap()
          .then((response) => {
            const ordersData = response.data?.orders || []
            const paginationData = response.data?.pagination

            resolve({
              currentPage: paginationData?.page || page,
              totalPages: paginationData?.totalPages || 1,
              results: ordersData
            })

            setNumberOfItems(ordersData.length)
          })
          .catch(() => {
            resolve({
              currentPage: page,
              totalPages: 1,
              results: []
            })

            setNumberOfItems(0)
          })
      })
    },
    [dispatch, pageSize]
  )

  const filterTabs: FilterTab<unknown>[] = [
    {
      id: 'all',
      label: 'All',
      filterDef: (items: unknown[]) => items as FulfillmentOrder[]
    },
    {
      id: 'accepted',
      label: 'Accepted',
      filterDef: (items: unknown[]) => {
        return (items as FulfillmentOrder[]).filter((order) => order.status?.name?.toLowerCase() === 'accepted')
      }
    },
    {
      id: 'picked_up',
      label: 'Picked Up',
      filterDef: (items: unknown[]) => {
        return (items as FulfillmentOrder[]).filter((order) => order.status?.name?.toLowerCase() === 'picked_up')
      }
    },
    {
      id: 'delivered',
      label: 'Delivered',
      filterDef: (items: unknown[]) => {
        return (items as FulfillmentOrder[]).filter((order) => order.status?.name?.toLowerCase() === 'delivered')
      }
    }
  ]

  const searchBar: SearchBarProps = {
    placeholder: 'Search by item description or order number',
    onSearchFilter: (items, query) => {
      const lowerQuery = query.toLowerCase()

      return (items as FulfillmentOrder[]).filter(
        (order) =>
          order.description?.toLowerCase().includes(lowerQuery) ||
          order.orderId?.toLowerCase().includes(lowerQuery) ||
          order.recipientName?.toLowerCase().includes(lowerQuery)
      )
    }
  }

  const emptyState: EmptyStateProps = {
    title: 'You have no fulfillment orders',
    subtitle: 'Create a new fulfillment order to get started.',
    button: {
      text: 'Create Order',
      onClick: () => {
        navigate(routes.createFulfillmentOrder.path)
      }
    }
  }

  const filterSections: (items: unknown[], filters: Filters) => FilterSectionConfig[] = useCallback(
    (items: unknown[]) => {
      const orders = items as FulfillmentOrder[]

      const uniqueRecipientNames = Array.from(new Set(orders.map((order) => order.recipientName).filter(Boolean)))

      const sections: FilterSectionConfig[] = [
        {
          id: 'recipient',
          title: 'Recipient',
          type: 'multi-select',
          options: [
            { primaryValue: 'All' },
            ...uniqueRecipientNames.map((recipientName) => ({ primaryValue: recipientName }))
          ],
          defaultValue: ['All'],
          filterDef: (item, filters) => {
            if (filters.recipient && Array.isArray(filters.recipient) && !filters.recipient.includes('All')) {
              return filters.recipient.includes((item as FulfillmentOrder).recipientName)
            }

            return true
          }
        }
      ]

      return sections
    },
    []
  )

  const hideAllFilters = (items: unknown[]) => {
    const orders = items as FulfillmentOrder[]

    return orders.length === 0
  }

  return (
    <Box sx={styles.container(numberOfItems)}>
      <PaginatedPage
        title={{ text: 'Fulfillment Orders', id: 'Fulfillment Orders' }}
        subtitle="Manage your fulfillment orders in one place."
        isFetchingData={loading}
        emptyState={emptyState}
        fetchCall={fetchFulfillmentOrders}
        getItemKey={(item) => (item as FulfillmentOrder).orderId}
        hideFilters={hideAllFilters}
        renderListData={FulfillmentOrderAccordion}
        horizontalFilterTabs={filterTabs}
        verticalFilters={filterSections}
        searchBar={searchBar}
      />
    </Box>
  )
}

export default FulfillmentOrders
